/**
 * accountRoutes.tsx — per-user account + profile routes (all authenticated users)
 * Extracted from App.tsx in #2470 (pure refactor, no behaviour changes).
 *
 * Covers: /account, /account/billing, /account/data-export,
 *         /profile, /profile/ssh-key, /billing, /usage,
 *         /upgrade, /upgrade-success, /danger-zone, /integrations,
 *         /support, /admin/internal/github-app-callback
 */
import React, { lazy, Suspense } from "react";
import { Route } from "react-router-dom";
import type { PageFallbackProps, ProtectedComponent } from "./types";

// Lazy-loaded account pages
const FleetProfile    = lazy(() => import("../pages/FleetProfile").then(m => ({ default: m.FleetProfile })));
const SshKeyProfile   = lazy(() => import("../pages/SshKeyProfile").then(m => ({ default: m.SshKeyProfile })));
const FleetDangerZone = lazy(() => import("../pages/FleetDangerZone").then(m => ({ default: m.FleetDangerZone })));
const FleetIntegrations = lazy(() => import("../pages/FleetIntegrations").then(m => ({ default: m.FleetIntegrations })));
const AdminBilling    = lazy(() => import("../pages/AdminBilling").then(m => ({ default: m.AdminBilling })));
const AdminUsage      = lazy(() => import("../pages/AdminUsage"));
const AdminUpgrade    = lazy(() => import("../pages/AdminUpgrade"));
const AdminUpgradeSuccess = lazy(() => import("../pages/AdminUpgradeSuccess").then(m => ({ default: m.AdminUpgradeSuccess })));
// #2442 — customer-facing usage + billing page
const AccountBilling  = lazy(() => import("../pages/AccountBilling").then(m => ({ default: m.AccountBilling })));
// #2443 — account self-serve: email change, TOTP MFA, API keys, sessions
const AccountSelfServe = lazy(() => import("../pages/AccountSelfServe").then(m => ({ default: m.AccountSelfServe })));
// #2458 — customer self-serve GDPR Article 20 data portability export
const AccountDataExport = lazy(() => import("../pages/AccountDataExport").then(m => ({ default: m.AccountDataExport })));
// #2444 — customer self-service ticket history
const AccountSupport  = lazy(() => import("../pages/AccountSupport").then(m => ({ default: m.AccountSupport })));
// #2603 — GitHub App ("2hives-ai-platform") install callback (sub-C3 of #2573)
const GitHubAppCallbackPage = lazy(() => import("../pages/GitHubAppCallbackPage").then(m => ({ default: m.GitHubAppCallbackPage })));
// #3057 #3058 — per-user OAuth popup landing surface (UNPROTECTED — popup
// may not share auth cookies; we only postMessage to the opener + close).
const OAuthCallbackPage = lazy(() => import("../pages/OAuthCallbackPage").then(m => ({ default: m.OAuthCallbackPage })));
// #3036 — hive-template persistence (Phase 2 of #3005). Caller-scoped: each
// user only sees their own + explicitly-shareable rows from anyone.
const AdminTemplates = lazy(() => import("../pages/AdminTemplates").then(m => ({ default: m.AdminTemplates })));

interface Props {
  PageFallback: React.ComponentType<PageFallbackProps>;
  Protected: ProtectedComponent;
}

/** Returns the array of per-user account <Route> elements. */
export function getAccountRoutes({ PageFallback, Protected }: Props): React.ReactElement[] {
  const F = <PageFallback />;

  return [
    // Profile
    <Route key="profile"         path="/profile"           element={<Protected><Suspense fallback={F}><FleetProfile /></Suspense></Protected>} />,
    <Route key="profile-sshkey"  path="/profile/ssh-key"   element={<Protected><Suspense fallback={F}><SshKeyProfile /></Suspense></Protected>} />,
    <Route key="danger-zone"     path="/danger-zone"       element={<Protected><Suspense fallback={F}><FleetDangerZone /></Suspense></Protected>} />,
    <Route key="integrations"    path="/integrations"      element={<Protected><Suspense fallback={F}><FleetIntegrations /></Suspense></Protected>} />,

    // Billing (legacy route kept alongside /account/billing)
    <Route key="billing"         path="/billing"           element={<Protected><Suspense fallback={F}><AdminBilling /></Suspense></Protected>} />,

    // Account sub-routes
    // #2443 — account self-serve (root must come after sub-paths in same group)
    <Route key="account-billing"     path="/account/billing"     element={<Protected><Suspense fallback={F}><AccountBilling /></Suspense></Protected>} />,
    <Route key="account-data-export" path="/account/data-export" element={<Protected><Suspense fallback={F}><AccountDataExport /></Suspense></Protected>} />,
    <Route key="account"             path="/account"             element={<Protected><Suspense fallback={F}><AccountSelfServe /></Suspense></Protected>} />,

    // Usage + upgrade
    <Route key="usage"           path="/usage"             element={<Protected><Suspense fallback={F}><AdminUsage /></Suspense></Protected>} />,
    <Route key="upgrade"         path="/upgrade"           element={<Protected><Suspense fallback={F}><AdminUpgrade /></Suspense></Protected>} />,
    <Route key="upgrade-success" path="/upgrade-success"   element={<Protected><Suspense fallback={F}><AdminUpgradeSuccess /></Suspense></Protected>} />,

    // Support — customer ticket history
    <Route key="support"         path="/support"           element={<Protected><Suspense fallback={F}><AccountSupport /></Suspense></Protected>} />,

    // #2603 — GitHub App ("2hives-ai-platform") install callback (sub-C3 of #2573).
    // GitHub redirects here with ?installation_id&state&setup_action; the page
    // POSTs to /admin/integrations/github-app/callback to persist the install
    // in the 2hives-github-installs DDB table (#2602), then bounces to
    // /integrations with a success/error flag.
    <Route key="github-app-callback" path="/admin/internal/github-app-callback"
           element={<Protected><Suspense fallback={F}><GitHubAppCallbackPage /></Suspense></Protected>} />,

    // #3057 #3058 — per-user OAuth popup landing surface. Intentionally
    // UNPROTECTED: the popup window often does not share AuthContext with
    // the opener (separate browsing context, cleared third-party cookies
    // on Safari/Firefox), so wrapping in <Protected> would bounce to /login
    // before we ever postMessage the opener — leaving the integrations
    // page stuck on "Not configured" (#3057) and the popup rendering the
    // SPA's login page instead of "you can close this window" (#3058).
    // This page only postMessages to `window.opener` (origin-locked) +
    // closes itself; it never reads tokens.
    <Route key="oauth-callback"      path="/oauth/callback"
           element={<Suspense fallback={F}><OAuthCallbackPage /></Suspense>} />,

    // #3036 — hive-template manager (caller-scoped; uses Protected, NOT
    // PlatformAdminProtected, because templates are per-user content).
    <Route key="admin-templates"     path="/admin/templates"
           element={<Protected><Suspense fallback={F}><AdminTemplates /></Suspense></Protected>} />,
  ];
}
